/**
 * "Open in the app" for the driver invite page.
 *
 * The invite link is an ordinary https URL on purpose — it must work for a driver without the app.
 * With the app installed, Android App Links open it in the app directly (see the intentFilters in
 * mobile/app.json). But that hand-off does not happen everywhere: a link typed or pasted into the
 * address bar, a redirect back from the login page, an in-app browser that swallows the intent.
 * Then the driver lands here, in the web cabinet, and this is the way across — the same thing the
 * "Открыть в приложении" bars of banks and marketplaces do.
 */

export const ANDROID_PACKAGE = 'com.lupa.mobile'

/** Where Chrome sends the driver when the app is not installed. */
export const RUSTORE_URL = `https://www.rustore.ru/catalog/app/${ANDROID_PACKAGE}`

/** The app ships for Android only (RuStore), so the bar is offered there and nowhere else. */
export function isAndroidBrowser(userAgent: string): boolean {
  return /Android/i.test(userAgent)
}

/** True when the page carries an invite in any of the shapes the backend generates. */
export function hasDriverInvite(search: string): boolean {
  const params = new URLSearchParams(search)
  return Boolean(params.get('invite') || params.get('code') || params.get('data'))
}

/**
 * Chrome's `intent:` URL for this very page, addressed to our package.
 *
 * Addressing the package explicitly is what makes this work even where App Link verification did
 * not (yet) happen: the app is asked directly, and its filter for https://lupaapp.ru/driver/accept
 * takes the same address the web page has — so the app's route reads the same `invite`/`data`
 * parameters. Without the app, Chrome follows `browser_fallback_url` to the store.
 */
export function buildAndroidAppIntent(
  location: { host: string; pathname: string; search: string },
  fallbackUrl: string = RUSTORE_URL,
): string {
  return (
    `intent://${location.host}${location.pathname}${location.search}` +
    `#Intent;scheme=https;package=${ANDROID_PACKAGE};` +
    `S.browser_fallback_url=${encodeURIComponent(fallbackUrl)};end`
  )
}
